import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import {
  ActionIcon,
  Flex,
  Group,
  Paper,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { useMemo, useState } from 'react';
import { BiChevronDown, BiChevronUp } from 'react-icons/bi';
import { PiDotsSix } from 'react-icons/pi';
import MandatoryMarker from '@/components/common/MandatoryMarker';
import { formatPrice } from '@/lib/helpers';
import {
  BlockTypes,
  IServiceBlock,
  useBlocksStore,
} from '@/models/commercial-proposals/blocks.store';
import {
  RENEWAL_PERIOD_LABELS,
  RenewalPeriod,
  TService,
  useServices,
} from '@/models/services';

const SERVICE_BLOCK_TYPES = [
  BlockTypes.Services,
  BlockTypes.ServicesInOnePackage,
  BlockTypes.ServicesInTwoPackages,
  BlockTypes.ServicesInThreePackages,
];

export default function RenewableServicesTable({
  order,
  onReorder,
}: {
  order: number[] | null;
  onReorder: (order: number[]) => void;
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const blocks = useBlocksStore((state) => state.blocks);

  const allServiceIds = useMemo(() => {
    const serviceBlocks = blocks.filter((b) =>
      SERVICE_BLOCK_TYPES.includes(b.type),
    ) as IServiceBlock[];
    return [
      ...new Set(
        serviceBlocks.flatMap((b) => b.services.map((s) => s.serviceId)),
      ),
    ];
  }, [blocks]);

  const { data: services, isLoading } = useServices(allServiceIds);

  const renewableServices = useMemo(() => {
    if (!services) return [];
    return (services.filter(Boolean) as TService[]).filter(
      (s) =>
        s?.renewal?.isRenewable === true &&
        s.renewal.period !== RenewalPeriod.None,
    );
  }, [services]);

  const orderedServices = useMemo(() => {
    if (!renewableServices.length) return [];
    if (!order) return renewableServices;

    const byId = new Map(renewableServices.map((s) => [s.id, s]));
    const ordered: TService[] = [];
    for (const id of order) {
      const s = byId.get(id);
      if (s) {
        ordered.push(s);
        byId.delete(id);
      }
    }
    for (const s of byId.values()) {
      ordered.push(s);
    }
    return ordered;
  }, [renewableServices, order]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;

    const reordered = [...orderedServices];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    onReorder(reordered.map((s) => s.id));
  };

  if (isLoading || !orderedServices.length) return null;

  return (
    <Paper
      p="xs"
      mb="xs"
      withBorder
      styles={{
        root: {
          background:
            'light-dark(var(--mantine-color-white), var(--mantine-color-dark-6))',
          borderRadius: '5px',
          boxShadow:
            'light-dark(0px 1px 3px 0px rgba(0, 0, 0, 0.1), 0px 1px 3px 0px rgba(0, 0, 0, 0.4))',
        },
      }}
    >
      <Flex justify="space-between" align="center">
        <Group gap={0}>
          <Text size="xs" fw={500} pl={0}>
            Renewable Services
          </Text>
        </Group>
        <Tooltip label={isCollapsed ? 'Expand' : 'Collapse'}>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? (
              <BiChevronUp size={16} />
            ) : (
              <BiChevronDown size={16} />
            )}
          </ActionIcon>
        </Tooltip>
      </Flex>
      {!isCollapsed && (
        <Table striped highlightOnHover mt="xs" fz="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 30 }} />
              <Table.Th>Service</Table.Th>
              <Table.Th>Renewal Period</Table.Th>
              <Table.Th>Renewal Price</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="renewable-services">
              {(provided) => (
                <Table.Tbody
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {orderedServices.map((service, index) => (
                    <Draggable
                      key={service.id}
                      draggableId={String(service.id)}
                      index={index}
                    >
                      {(provided) => (
                        <Table.Tr
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          <Table.Td
                            {...provided.dragHandleProps}
                            style={{
                              width: 30,
                              textAlign: 'center',
                              color: 'var(--mantine-color-gray-5)',
                              cursor: 'grab',
                            }}
                          >
                            <PiDotsSix size={14} />
                          </Table.Td>
                          <Table.Td>
                            {service.name}
                            <MandatoryMarker
                              shown={service.isMandatory}
                              ml={5}
                            />
                          </Table.Td>
                          <Table.Td>
                            {RENEWAL_PERIOD_LABELS[service.renewal!.period] ??
                              '—'}
                          </Table.Td>
                          <Table.Td>
                            {formatPrice(
                              service.renewal!.renewalPrice ?? service.price,
                              service.currency,
                            )}
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Table.Tbody>
              )}
            </Droppable>
          </DragDropContext>
        </Table>
      )}
    </Paper>
  );
}
